import { useTranslation } from 'react-i18next'
import { DialogDescription, DialogTitle } from '@ui/components/ui/dialog'
import { Button } from '@ui/components/ui/button'
import { CheckCircle2Icon, LoaderCircleIcon } from 'lucide-react'
import type { FirmwareVersions } from '@/lib/firmware-update/use-firmware-versions'
import type { FirmwareInstallState } from '@/lib/firmware-update/use-firmware-install'

type Props = {
  versions: FirmwareVersions & { updateAvailable: true }
  state: FirmwareInstallState
  onInstall: () => void
  onClose: () => void
  isOnline: boolean
}

const buttonClass =
  'w-full p-8 text-md font-bold rounded-xl hover:bg-primary hover:text-primary-foreground select-none'

export function FirmwareUpdateContent({ versions, state, onInstall, onClose, isOnline }: Props) {
  const { t } = useTranslation()

  return (
    <>
      {/* Header */}
      <div className="flex flex-col space-y-2 text-center">
        <DialogTitle className="text-3xl mb-4 text-center select-none">
          {state.phase === 'done'
            ? t('settings.machine.firmware-update-complete', 'Update Complete')
            : t('settings.machine.firmware-update-available', 'Firmware Update Available')}
        </DialogTitle>

        {/* Icon */}
        {(state.phase === 'downloading' || state.phase === 'sending') && (
          <div className="flex justify-center pt-10 pb-12">
            <LoaderCircleIcon className="animate-spin h-10 w-10" />
          </div>
        )}
        {state.phase === 'done' && (
          <div className="flex justify-center pt-10 pb-12">
            <CheckCircle2Icon className="h-10 w-10 text-accent" />
          </div>
        )}

        {/* Description */}
        <DialogDescription className="text-xl text-center">
          {state.phase === 'idle' && (
            <>
              {versions.current} &rarr; {versions.target}
            </>
          )}
          {state.phase === 'error' && state.message}
          {state.phase === 'downloading' &&
            t('settings.machine.firmware-downloading', 'Downloading firmware...')}
          {state.phase === 'sending' &&
            t('settings.machine.firmware-sending', 'Sending to machine...')}
          {state.phase === 'done' &&
            t(
              'settings.machine.firmware-restart-prompt',
              'Please restart the machine to apply the update.'
            )}
        </DialogDescription>

        {state.phase === 'idle' && (
          <p className="text-lg text-center text-muted-foreground pt-4">
            {t(
              'settings.machine.firmware-update-warning',
              'Make sure the machine is not in use. Only install updates in a controlled environment.'
            )}
          </p>
        )}

        {!isOnline && state.phase === 'idle' && (
          <p className="text-lg text-center text-muted-foreground">
            {t('settings.machine.firmware-no-internet', 'No internet connection.')}
          </p>
        )}
      </div>

      {/* Footer */}
      {(state.phase === 'idle' || state.phase === 'error') && (
        <div className="mt-8">
          <Button onClick={onInstall} disabled={!isOnline} className={buttonClass}>
            {state.phase === 'error'
              ? t('settings.machine.firmware-retry', 'Retry')
              : t('settings.machine.firmware-install', 'Install Update')}
          </Button>
        </div>
      )}

      {state.phase === 'done' && (
        <div className="mt-8">
          <Button onClick={onClose} className={buttonClass}>
            {t('settings.machine.firmware-close', 'Close')}
          </Button>
        </div>
      )}
    </>
  )
}
